import React, { useContext, useEffect, useRef } from 'react';

import AppContext from '../../context';
import {
    trackBannerClick,
    trackBannerImpression,
} from '../../tracking/bannerTracking';
import { CategoryTypeEnum, UiBanner, UiBannerItem } from '../../types';
import { ANIMAL_TYPE_TO_BANNER_KEY } from '../../utils/categoryUtils';

import styles from './BurgerBanner.module.scss';
import { PanelData } from './types';

interface BurgerBannerProps {
    openPanels: PanelData[];
}

export function resolveMobileBanner(
    openPanels: PanelData[],
    banner: UiBanner | null | undefined,
): UiBannerItem | null {
    if (!banner) {
        return null;
    }

    const currentPanel = openPanels.slice(-1)[0];
    const { currentType, currentAnimalType } = currentPanel;

    if (
        currentType === CategoryTypeEnum.OFFERS ||
        currentType === CategoryTypeEnum.OUTLET
    ) {
        return banner.specials;
    }

    if (currentType === CategoryTypeEnum.BRANDS) {
        return null;
    }

    if (!currentType && !currentAnimalType) {
        return banner.root;
    }

    if (currentType === CategoryTypeEnum.ANIMAL && currentAnimalType) {
        const key = ANIMAL_TYPE_TO_BANNER_KEY[currentAnimalType.toUpperCase()];
        if (key) {
            return banner[key];
        }
    }

    return null;
}

export default function BurgerBanner({
    openPanels,
}: BurgerBannerProps): React.JSX.Element | null {
    const { banner, siteId, locale } = useContext(AppContext);

    const bannerItem = resolveMobileBanner(openPanels, banner);
    const currentPanel = openPanels.slice(-1)[0];
    const animalType = currentPanel?.currentAnimalType;
    const categoryType = currentPanel?.currentType;

    const trackedImpressionsRef = useRef(new Set<string>());

    useEffect(() => {
        const mobileImage = bannerItem?.mobileImage;

        if (!mobileImage) {
            return;
        }

        const impressionKey = [
            mobileImage,
            categoryType ?? '',
            animalType ?? '',
            siteId ?? '',
            locale ?? '',
        ].join('|');

        if (trackedImpressionsRef.current.has(impressionKey)) {
            return;
        }

        trackedImpressionsRef.current.add(impressionKey);

        trackBannerImpression({ categoryType, animalType, siteId, locale });
    }, [bannerItem?.mobileImage, categoryType, animalType, siteId, locale]);

    if (!bannerItem?.mobileImage) {
        return null;
    }

    const { targetLink, linkAriaLabel, mobileImage, mobileImageAltText } =
        bannerItem;

    const handleClick = () => {
        trackBannerClick({ categoryType, animalType, siteId, locale });
    };

    return (
        <div className={styles.burgerBanner}>
            <a
                href={targetLink}
                aria-label={linkAriaLabel ?? undefined}
                className={styles.burgerBannerLink}
                data-zta="burger-navigation-banner"
                onClick={handleClick}>
                <img
                    src={mobileImage}
                    alt={mobileImageAltText ?? ''}
                    className={styles.burgerBannerImage}
                />
            </a>
        </div>
    );
}
